import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Position a `position: fixed` popover under an anchor element, kept wholly
 * inside the viewport (a fixed element can't be scrolled into view): shifted up
 * when it would run off the bottom, re-measured on scroll/resize. Also closes
 * on outside mousedown and Escape.
 */
export function useAnchoredPopover(
  anchor: HTMLElement,
  ref: RefObject<HTMLDivElement | null>,
  onClose: () => void,
  width = 272,
): { top: number; left: number } {
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    function place() {
      const r = anchor.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const h = ref.current?.offsetHeight ?? 0;
      let top = r.bottom + 4;
      if (top + h > window.innerHeight - 8) top = Math.max(8, window.innerHeight - h - 8);
      setPos({ top, left });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [anchor, ref, width]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node) || anchor.contains(e.target as Node)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [anchor, ref, onClose]);

  return pos;
}
